// =============================================================================
// CORE
// =============================================================================
import { createRouter, createWebHistory } from 'vue-router';
import { setAnonymousMode } from '@/auth/token';
import { authGuard } from '../auth/authGuard';

// =============================================================================
// ANONYMOUS
// =============================================================================

const TributeStoriesContribute = () => import('@/views/Anonymous/TributeStoriesContribute.vue');
const LandingPage = () => import('@/views/Anonymous/LandingPage.vue');
const ViewStory = () => import('@/views/Anonymous/ViewStory.vue');
const ViewStoryQr = () => import('@/views/Anonymous/ViewStoryQr.vue');

// =============================================================================
// ANALYTICS
// =============================================================================
const Analytics = () => import('../views/Analytics/Analytics.vue');

// =============================================================================
// ADMIN
// =============================================================================
const AdminBilling = () => import('../views/Admin/AdminBilling.vue');
const AdminSettings = () => import('../views/Admin/AdminSettings.vue');
const DailyStreamSheet = () => import('../views/Admin/DailyStreamSheet.vue');
const HomesList = () => import('../views/Admin/HomesList.vue');
const HomesByOwner = () => import('../views/Admin/HomesByOwner.vue');
const ManageAdminUsers = () => import('../views/Admin/ManageAdminUsers.vue');
const CreateEmbedCode = () => import('../views/Admin/CreateEmbedCode.vue');
const MiscAdmin = () => import('../views/Admin/Misc.vue');
const TributeAdmin = () => import('../views/Admin/TributeAdmin.vue');
const GuestbookAdmin = () => import('../views/Admin/GuestbookAdmin.vue');
const Guestbook = () => import('../views/FuneralHome/Guestbook.vue');
const Resources = () => import('../views/FuneralHome/Resources.vue');
const TributeVideoSongAnalytics = () => import('../views/Admin/TributeVideoSongAnalytics.vue');

// =============================================================================
// SERVICE
// =============================================================================
const AnonymousStream = () => import('../views/Services/AnonymousStream.vue');
const Billing = () => import('../views/Services/Billing.vue');
const IFrameVideoPlayer = () => import('../views/Services/IFrameVideoPlayer.vue');
const LiveDashboard = () => import('../views/Services/LiveDashboard.vue');
const ManageServicesRefactor = () => import('../views/Services/ManageServiceRefactored.vue');
const Renders = () => import('../views/Services/Renders.vue');
const Services = () => import('../views/Services/Services.vue');
const ServicesByHome = () => import('../views/Services/ServicesByHome.vue');

const ServiceVideoUploader = () => import('../views/Services/ServiceVideoUploader.vue');
const ViewStream = () => import('../views/Services/ViewStream.vue');
const CreateService = () => import('../views/Services/CreateService.vue');

const TributeVideoNew = () => import('../views/Services/TributeVideo.vue');
const TributeSlideshow = () => import('../views/Services/TributeSlideshow.vue');

// =============================================================================
// UTILITIES
// =============================================================================
const ConverterPdf = () => import('../views/Utilities/ConverterPdf.vue');
const CreateUser = () => import('../views/Utilities/CreateUser.vue');
import LogoutRefactored from '../views/Utilities/LogoutRefactored.vue';
import MigrationNotification from '../views/Utilities/MigrationNotification.vue';
const PathNotFound = () => import('../views/Utilities/PathNotFound.vue');
const Settings = () => import('../views/Utilities/Settings.vue');
const TermsAndConditions = () => import('../views/Utilities/TermsAndConditions.vue');
const GoogleReport = () => import('../views/Utilities/GoogleReport.vue');

// =============================================================================
// USERS
// =============================================================================
const Feedback = () => import('../views/Users/Feedback.vue');
const ManageUsers = () => import('../views/Users/ManageUsers.vue');
const Users = () => import('../views/Users/Users.vue');

// =============================================================================
// OWNERS
// =============================================================================
const CreateOwner = () => import('../views/Owners/CreateOwner.vue');
const ManageOwners = () => import('../views/Owners/ManageOwners.vue');
const Owners = () => import('../views/Owners/Owners.vue');
const OwnerSettings = () => import('../views/Owners/OwnerSettings.vue');
const OwnerReportPowerbi = () => import('../views/Owners/OwnerReportPowerbi.vue');
const OwnerReportInHouse = () => import('../views/Owners/OwnerReportInHouse.vue');

// =============================================================================
// HOMES
// =============================================================================
const CreateEditFuneralHome = () => import('../views/FuneralHome/CreateEditFuneralHome.vue');
import HomeSignUp from '../views/FuneralHome/HomeSignUp.vue';

// =============================================================================
// DEBUGGING
// =============================================================================
const Test = () => import('../views/Debugging/Test.vue');

// =============================================================================
// Ecommerce
// =============================================================================

const CreateEditSupplier = () => import('@/views/Shop/CreateEditSupplier.vue');
const EcomAdmin = () => import('@/views/Shop/EcomAdmin.vue');
const FuneralHomeEcom = () => import('@/views/Shop/FuneralHomeEcom.vue');
const SupplierSettings = () => import('@/views/Shop/SupplierSettings.vue');
const OrderDetails = () => import('@/views/Shop/OrderDetails.vue');
const PurchaseDetails = () => import('@/views/Shop/PurchaseDetails.vue');
const FuneralHomeShop = () => import('@/views/Shop/FuneralHomeShop.vue');
const CreateEditProduct = () => import('@/views/Shop/CreateEditProduct.vue');
const PurchaseStatus = () => import('@/views/Shop/PurchaseStatus.vue');
const ShipmentDetails = () => import('@/views/Shop/ShipmentDetails.vue');

// =============================================================================
// ROUTES
// =============================================================================
const routes = [
    {
        path: '/debugging/test',
        beforeEnter: authGuard,
        component: Test,
    },
    {
        path: '/debugging/dates',
        beforeEnter: authGuard,
        component: () => import('../views/Debugging/Dates.vue'),
    },
    {
        path: '/',
        name: 'appRoot',
        //Handle base redirect in authGuard to minimize nav redirect interuption
        // redirect: '/services',
        beforeEnter: authGuard,
    },
    {
        path: '/logout',
        name: 'Logout',
        beforeEnter: authGuard,
        component: LogoutRefactored,
    },
    {
        path: '/services',
        name: 'Services',
        component: Services,
        beforeEnter: authGuard,
    },
    {
        path: '/tribute/:slug/:section?',
        name: 'TributeVideo',
        component: TributeVideoNew,
        beforeEnter: authGuard,
    },
    {
        path: '/tribute/family-share/:slug/:pin/:section?',
        name: 'TributeVideoFamily',
        component: TributeVideoNew,
        props: { requiredRoleLevel: 'TributeAdmin' },
        meta: { anonymous: true },
    },
    {
        path: '/tribute/contributor/:slug/:pin/:section?',
        name: 'TributeVideoContributor',
        component: TributeVideoNew,
        props: { requiredRoleLevel: 'Tribute' },
        meta: { anonymous: true },
    },
    {
        path: '/tribute-slideshow/:slug',
        name: 'TributeSlideshow',
        component: TributeSlideshow,
        beforeEnter: authGuard,
    },
    {
        path: '/memoryshare/videoplayer/:slug',
        name: 'EmbedPlayer',
        component: IFrameVideoPlayer,
    },
    {
        path: '/terms-and-conditions',
        name: 'TermsAndConditions',
        component: TermsAndConditions,
    },
    {
        path: '/services/create/:funeral_home_id?',
        name: 'CreateService',
        component: CreateService,
        beforeEnter: authGuard,
    },
    {
        path: '/services/homes/:funeral_home_id',
        name: 'ServicesByHome',
        component: ServicesByHome,
        beforeEnter: authGuard,
    },
    {
        path: '/services/:slug/add-pdf',
        name: 'AddPDF',
        component: ConverterPdf,
        beforeEnter: authGuard,
    },
    {
        path: '/services/:slug/billing',
        name: 'Billing',
        component: Billing,
        beforeEnter: authGuard,
    },
    {
        path: '/admin/settings/:tab?',
        name: 'MiscAdminSettings',
        component: MiscAdmin,
        beforeEnter: authGuard,
    },

    {
        path: '/services/upload/:id',
        name: 'Uploader',
        component: ServiceVideoUploader,
        beforeEnter: authGuard,
    },
    {
        path: '/services/:slug/:event?',
        name: 'EditServices',
        component: ManageServicesRefactor,
        beforeEnter: authGuard,
    },
    {
        path: '/services/remote/:slug/:token',
        name: 'AnonymousStream',
        component: AnonymousStream,
    },
    {
        path: '/view-story/:slug',
        name: 'ViewStory',
        component: ViewStory,
    },
    {
        path: '/view-story-qr/:slug',
        name: 'ViewStoryQr',
        component: ViewStoryQr,
    },
    {
        path: '/analytics',
        name: 'Analytics',
        component: Analytics,
        beforeEnter: authGuard,
    },
    {
        path: '/analytics/:slug',
        name: 'ServiceAnalytics',
        component: Analytics,
        beforeEnter: authGuard,
    },
    {
        path: '/homes',
        name: 'Homes',
        component: HomesList,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/google-report/:funeralHomeId',
        name: 'GoogleReport',
        component: GoogleReport,
        beforeEnter: authGuard,
    },
    {
        path: '/homes/:owner_id',
        name: 'HomesByOwner',
        component: HomesByOwner,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/landing-page/:id',
        name: 'LandingPage',
        component: LandingPage,
    },

    {
        path: '/tribute-story-upload/:slug',
        name: 'TributeStoryUpload',
        component: TributeStoriesContribute,
    },
    {
        path: '/signup',
        name: 'Signup',
        component: HomeSignUp,
    },
    {
        path: '/daily-stream-sheet',
        name: 'DailyStreamSheet',
        component: DailyStreamSheet,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/owners',
        name: 'Owners',
        component: Owners,
        meta: { admin: true },
        beforeEnter: authGuard,
    },
    {
        path: '/owner-report/:id',
        name: 'OwnerReport',
        component: OwnerReportInHouse,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/owner-report-powerbi/:id',
        name: 'OwnerReportPowerbi',
        component: OwnerReportPowerbi,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/renders',
        name: 'Renders',
        component: Renders,
        meta: { admin: true },
        beforeEnter: authGuard,
    },
    {
        path: '/manage-homes/:id',
        name: 'EditHomes',
        component: CreateEditFuneralHome,
        meta: { admin: true },
        beforeEnter: authGuard,
    },
    {
        path: '/homes/create/:ownerId?',
        name: 'CreateHome',
        component: CreateEditFuneralHome,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },

    {
        path: '/owners/create',
        name: 'CreateOwner',
        component: CreateOwner,
        beforeEnter: authGuard,
    },
    {
        path: '/owners/settings/:id',
        name: 'EditOwner',
        component: ManageOwners,
        meta: { admin: true },
        beforeEnter: authGuard,
    },

    {
        path: '/admins',
        name: 'Admins',
        component: Users,
        beforeEnter: authGuard,
    },
    {
        path: '/admins/billing-events',
        name: 'AdminBilling',
        component: AdminBilling,
        meta: { admin: true },
        beforeEnter: authGuard,
    },
    {
        path: '/manage-users/create',
        name: 'ManageUsers',
        component: ManageUsers,
        meta: { admin: true },
        beforeEnter: authGuard,
    },
    {
        path: '/admins/create',
        name: 'ManageAdminUsers',
        component: ManageAdminUsers,
        meta: { admin: true },
        beforeEnter: authGuard,
    },

    {
        path: '/admins/edit/:id',
        name: 'EditAdminUsers',
        component: ManageAdminUsers,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/manage-users/edit/:id',
        name: 'EditUsers',
        component: ManageUsers,
        meta: { admin: true },
        beforeEnter: authGuard,
    },
    {
        path: '/accept',
        name: 'CreateUser',
        component: CreateUser,
    },
    {
        path: '/settings',
        name: 'Settings',
        component: Settings,
        beforeEnter: authGuard,
    },

    {
        path: '/feedback',
        name: 'Feedback',
        component: Feedback,
        beforeEnter: authGuard,
    },
    {
        path: '/homes/settings/:id',
        name: 'AdminSettings',
        component: AdminSettings,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/owner-settings/:id',
        name: 'OwnerSettings',
        component: OwnerSettings,
        meta: { admin: true, owner: true },
        beforeEnter: authGuard,
    },
    {
        path: '/ecom',
        name: 'EcomAdmin',
        component: EcomAdmin,
        beforeEnter: authGuard,
        meta: { admin: true },
    },
    {
        path: '/orders',
        name: 'FuneralHomeEcom',
        component: FuneralHomeEcom,
        beforeEnter: authGuard,
    },
    {
        path: '/tribute',
        name: 'TributeAdmin',
        component: TributeAdmin,
        beforeEnter: authGuard,
        meta: { admin: true },
    },
    {
        path: '/guestbook',
        name: 'GuestbookAdmin',
        component: GuestbookAdmin,
        beforeEnter: authGuard,
        meta: { admin: true, owner: true },
    },
    {
        path: '/family-engagement/:id?',
        name: 'Guestbook',
        component: Guestbook,
        beforeEnter: authGuard,
    },
    {
        path: '/resources',
        name: 'Resources',
        component: Resources,
        beforeEnter: authGuard,
    },
    {
        path: '/song/:id',
        name: 'TributeVideoSongAnalytics',
        component: TributeVideoSongAnalytics,
        beforeEnter: authGuard,
        meta: { admin: true },
    },
    {
        path: '/shop-home/',
        name: 'FuneralHomeShop',
        component: FuneralHomeShop,
        beforeEnter: authGuard,
    },
    {
        path: '/order-details/:id',
        name: 'OrderDetails',
        component: OrderDetails,
        beforeEnter: authGuard,
    },
    {
        path: '/purchase-details/:id',
        name: 'PurchaseDetails',
        component: PurchaseDetails,
        beforeEnter: authGuard,
    },

    {
        path: '/shipment/:id',
        name: 'ShipmentDetails',
        component: ShipmentDetails,
        beforeEnter: authGuard,
    },
    {
        path: '/purchase-status/:slug',
        name: 'PurchaseStatus',
        component: PurchaseStatus,
    },
    {
        path: '/suppliers/edit/:id',
        name: 'EditSupplier',
        component: CreateEditSupplier,
        beforeEnter: authGuard,
    },
    {
        path: '/suppliers/create',
        name: 'CreateSupplier',
        component: CreateEditSupplier,
        beforeEnter: authGuard,
    },
    {
        path: '/supplier-settings/:supplier_id',
        name: 'SupplierSettings',
        component: SupplierSettings,
        beforeEnter: authGuard,
    },
    {
        path: '/products/create/:supplier_id',
        name: 'CreateProduct',
        component: CreateEditProduct,
        beforeEnter: authGuard,
    },
    {
        path: '/products/edit/:supplier_id/:product_id',
        name: 'EditProduct',
        component: CreateEditProduct,
        beforeEnter: authGuard,
    },
    {
        path: '/dashboard/live',
        name: 'LiveDashboard',
        component: LiveDashboard,
        beforeEnter: authGuard,
    },
    {
        path: '/migration-notification',
        name: 'MigrationNotification',
        component: MigrationNotification,
    },
    {
        path: '/wrapped',
        name: 'Wrapped',
        beforeEnter: authGuard,
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'PathNotFound',
        component: PathNotFound,
    },
];

const router = new createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    base: process.env.BASE_URL,
    routes,
});

// Anonymous routes (family/contributor links) skip Auth0 token retrieval entirely.
router.beforeEach(to => {
    setAnonymousMode(to.meta.anonymous === true);
});

router.onError((error, to) => {
    // Forces a hard reload on the new URL when a route failed to load
    // This is likely due to being on the page when there's been an update in the background
    // so the chunks have changed their hashes.
    if (
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk')
    ) {
        window.location.href = to.fullPath;
    }
});
export default router;
